import React, { useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import AuthLayout, { AuthLink, ErrorBox, SubmitButton } from '../components/auth/AuthLayout'
import { verifyEmail, errorText } from '../api/auth'
import Loader from '../components/Loader'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { odczytajPowrot } from '../lib/powrotPoRejestracji'
import { useAuth } from '../context/AuthContext'

/** `dalej` z linku tylko jako ścieżka w serwisie („/…”, bez „//” i schematu), inaczej null. */
export const adresDalej = (a) => (typeof a === 'string' && /^\/(?![/\\])\S*$/.test(a) ? a : null)

/**
 * Strona pod linkiem z maila: /potwierdz-email/:key
 *
 * Potwierdzenie wykonuje się ŻĄDANIEM POST, a nie samym wejściem na stronę.
 * Powód: skanery antywirusowe w skrzynkach pocztowych odwiedzają linki
 * z wiadomości, żeby sprawdzić, czy nie prowadzą do złośliwej treści. Gdyby
 * potwierdzenie działo się przy zwykłym wejściu (GET), taki skaner potwierdzałby
 * konto ZA użytkownika — bez jego wiedzy i zanim w ogóle otworzy maila.
 *
 * Dlatego strona pokazuje przycisk. Jeden dodatkowy klik za realną gwarancję,
 * że adres potwierdził człowiek.
 *
 * `?dalej=/rynek/moje/<kod>` (zlecenie gościa, decyzja właściciela 2026-10-26): potwierdzenie publikuje zlecenie,
 * a backend loguje tylko w przeglądarce, w której zakładano konto. Wtedy przechodzimy prosto pod `dalej`; na innym
 * urządzeniu logowanie odeśle tam po zalogowaniu.
 */
const VerifyEmail = () => {
  useDocumentTitle('Potwierdzenie adresu e-mail')
  const { key } = useParams()
  const navigate = useNavigate()
  const { search } = useLocation()
  const { refresh } = useAuth()
  const dalej = adresDalej(new URLSearchParams(search).get('dalej'))
  const zlecenie = Boolean(dalej?.startsWith('/rynek/moje'))

  const [state, setState] = useState('idle') // idle | busy | done | error
  const [error, setError] = useState('')
  const redirectTimer = useRef(null)

  useEffect(() => () => clearTimeout(redirectTimer.current), [])

  // Miejsce, z którego ktoś poszedł zakładać konto (lib/powrotPoRejestracji): logowanie odeśle tam zamiast na pulpit.
  const powrot = dalej || odczytajPowrot()
  const doLogowania = powrot ? { from: powrot } : undefined

  const confirm = async () => {
    setState('busy')
    setError('')
    const res = await verifyEmail(key)

    // allauth po potwierdzeniu nie loguje automatycznie (ACCOUNT_LOGIN_ON_EMAIL_
    // CONFIRMATION=False), więc odpowiedź bywa 401 mimo sukcesu. O powodzeniu
    // świadczy brak błędów, nie kod HTTP.
    if (res.errors && res.errors.length) {
      setError(errorText(res, 'Nie udało się potwierdzić adresu.'))
      setState('error')
      return
    }
    if (dalej && res.meta?.is_authenticated) {
      await refresh?.()
      navigate(dalej, { replace: true })
      return
    }
    setState('done')
    redirectTimer.current = setTimeout(() => navigate('/logowanie', { replace: true, state: doLogowania }), 4000)
  }

  if (state === 'done') {
    return (
      <AuthLayout title="Adres potwierdzony"
                  subtitle={zlecenie ? 'Zlecenie trafiło do wykonawców. Zaloguj się, żeby je zobaczyć.' : 'Konto jest gotowe. Możesz się zalogować.'}>
        <div className="flex justify-center py-4">
          <CheckCircleIcon className="w-14 h-14 text-emerald-500" />
        </div>
        <p className="text-sm text-gray-400 text-center">Za chwilę przeniesiemy Cię do logowania…</p>
        <div className="mt-4 text-center text-sm">
          <AuthLink to="/logowanie" state={doLogowania}>Przejdź teraz</AuthLink>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      title="Potwierdź adres e-mail"
      subtitle={zlecenie ? 'Kliknij przycisk, a Twoje zlecenie trafi do wykonawców.' : 'Kliknij przycisk, żeby dokończyć zakładanie konta.'}
      footer={<AuthLink to="/logowanie">Wróć do logowania</AuthLink>}
    >
      <ErrorBox>{error}</ErrorBox>

      {state === 'busy' ? (
        <div className="flex justify-center py-6"><Loader label="" /></div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); confirm() }}>
          <SubmitButton busy={false}>Potwierdzam swój adres</SubmitButton>
        </form>
      )}

      {state === 'error' && (
        <p className="mt-4 text-sm text-gray-500 leading-6">
          Link mógł wygasnąć, jest ważny 3 dni. Załóż konto ponownie albo{' '}
          <AuthLink to="/reset-hasla">poproś o nowy link</AuthLink>.
        </p>
      )}
    </AuthLayout>
  )
}

export default VerifyEmail
